import { Controller } from "@hotwired/stimulus"
import * as SessionStore from "../lib/session_store"
import { SectionMap } from "../lib/section_map"

// Opener prompts offered as tappable chips on a fresh conversation. Module-scope
// (not a private static) — accessing a private static via `this.constructor`
// throws under the esbuild bundle, and no other controller in the app uses that
// pattern.
// Last-resort opener fallback when the server sends no session-specific openers
// (see Coach::SessionOpeners). Track-agnostic on purpose — no corner names.
const CC_QUICK_REPLIES = [
  "What should I work on first?",
  "Where am I losing the most time?",
  "How was my pace this session?",
  "Where's the theoretical best?",
]

// SVG path data per tool kind (ported from cc-chat.jsx TOOL_ICON). Module-scope
// for the same reason as CC_QUICK_REPLIES above.
// Max pixel height the input textarea grows to before it starts scrolling.
// The textarea has NO vertical padding (the surrounding pill provides it), and
// a whole-pixel line-height of 18px, so 4 lines == exactly 72px and a 5th line
// never peeks past the edge while scrolling. Keep in sync with the
// `leading-[18px]` + `max-h-[72px]` classes on the textarea in
// _coaching_chat.html.erb.
const MAX_INPUT_HEIGHT = 72

// SVG path data per tool kind (ported from cc-chat.jsx TOOL_ICON). Module-scope
// for the same reason as CC_QUICK_REPLIES above.
const TOOL_ICON = {
  telemetry: "M3 12h3l2-7 4 14 2-7h4",
  compare: "M4 7h10M4 7l3-3M4 7l3 3M20 17H10m10 0l-3-3m3 3l-3 3",
  map: "M9 4 4 6v14l5-2 6 2 5-2V4l-5 2-6-2z",
  compute: "M9 3h6v6h6v6h-6v6H9v-6H3V9h6z",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3",
  session: "M3 7l9-4 9 4-9 4-9-4zM3 12l9 4 9-4M3 17l9 4 9-4",
}

// Coaching Chat island. Ported from docs/design/coaching-chat/ (cc-app.jsx,
// cc-chat.jsx, cc-data.jsx). The send() seam streams real coaching replies over
// SSE from POST /sessions/:id/messages (see #streamReply).
export default class extends Controller {
  static targets = [
    "backdrop",
    "drawer",
    "scrim",
    "fab",
    "messagesList",
    "historyPanel",
    "input",
    "expandBtn",
    "sendBtn",
    "resizeHandle",
  ]
  static values = {
    session: Object,
    laps: Array,
    openers: Array,
    sessionId: String,
  }
  #abort = null
  #watchdog = null
  #historyCursor = null

  connect() {
    this.open = false
    this.expanded = this.#storedExpanded()
    this.isReplying = false
    this.showHistory = false
    this._sectionMaps = new Map()

    // Start with one client-only "New debrief"; real history loads async.
    this.convos = [this.#ccNewConvo()]
    this.activeId = this.convos[0].id
    this.#renderMessages()
    this.#loadHistory()

    // ESC to close
    this.onKeydown = (e) => {
      if (e.key === "Escape" && this.open) this.closeDrawer()
    }
    window.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    this.#cancelStream()
    this.#destroySectionMaps()
    window.removeEventListener("keydown", this.onKeydown)
  }

  // ── open/close/expand ──────────────────────────────────────────────────

  openDrawer() {
    this.open = true
    this.#applyOpenState()
  }

  closeDrawer() {
    this.#cancelStream()
    this.open = false
    this.expanded = false
    this.showHistory = false
    this.#applyOpenState()
    if (this.hasHistoryPanelTarget)
      this.historyPanelTarget.classList.add("hidden")
  }

  toggleExpand() {
    this.expanded = !this.expanded
    this.#persistExpanded()
    this.#applyOpenState()
  }

  #applyOpenState() {
    const w = this.#drawerWidth()
    this.drawerTarget.style.width = `${w}px`
    this.drawerTarget.style.transform = this.open
      ? "translateX(0)"
      : `translateX(${w + 40}px)`
    this.drawerTarget.style.boxShadow = this.open
      ? "0 30px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)"
      : "none"
    this.scrimTarget.style.opacity = this.open ? "0.45" : "0"
    this.scrimTarget.style.pointerEvents = this.open ? "auto" : "none"
    this.backdropTarget.classList.toggle("cc-receded", this.open)
    // FAB hides while open
    this.fabTarget.style.opacity = this.open ? "0" : "1"
    this.fabTarget.style.transform = this.open
      ? "scale(0.7) translateY(8px)"
      : "scale(1)"
    this.fabTarget.style.pointerEvents = this.open ? "none" : "auto"
    // Swap expand/collapse icon on the expand button (matches prototype HeaderBtn swap)
    if (this.hasExpandBtnTarget) {
      this.expandBtnTarget.title = this.expanded ? "Collapse" : "Expand"
      this.expandBtnTarget.innerHTML = this.expanded
        ? `<svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 9L4 4M9 9V5M9 9H5M15 15l5 5M15 15v4M15 15h4" /></svg>`
        : `<svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6M21 3l-7 7M9 21H3v-6M3 21l7-7" /></svg>`
    }
  }

  // ── Drawer width + drag-to-snap resize ──────────────────────────────────

  #NARROW_WIDTH = 440
  #wideWidth() {
    return Math.min(820, Math.round(window.innerWidth * 0.62))
  }
  #drawerWidth() {
    return this.expanded ? this.#wideWidth() : this.#NARROW_WIDTH
  }

  #persistExpanded() {
    try {
      localStorage.setItem("cc:expanded", this.expanded ? "1" : "0")
    } catch (_) {}
  }
  #storedExpanded() {
    try {
      return localStorage.getItem("cc:expanded") === "1"
    } catch (_) {
      return false
    }
  }

  // Pointer drag on the left-edge handle. The drawer follows the cursor 1:1
  // (clamped to the two widths); on release it snaps to whichever it's closest to.
  startResize(e) {
    if (e.button !== 0 || !this.open) return
    e.preventDefault()
    this._rzStartX = e.clientX
    this._rzStartW = this.drawerTarget.getBoundingClientRect().width
    this._rzLiveW = this._rzStartW
    this.drawerTarget.classList.add("is-resizing") // kills the width transition mid-drag
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
    this.resizeHandleTarget.setPointerCapture?.(e.pointerId)
    this._onRzMove ||= (ev) => this.#resizeMove(ev)
    this._onRzUp ||= () => this.#resizeEnd()
    window.addEventListener("pointermove", this._onRzMove)
    window.addEventListener("pointerup", this._onRzUp)
  }

  #resizeMove(e) {
    const min = this.#NARROW_WIDTH,
      max = this.#wideWidth()
    const dx = e.clientX - this._rzStartX // left edge: leftward (dx<0) widens
    this._rzLiveW = Math.max(min, Math.min(max, this._rzStartW - dx))
    this.drawerTarget.style.width = `${this._rzLiveW}px`
  }

  #resizeEnd() {
    window.removeEventListener("pointermove", this._onRzMove)
    window.removeEventListener("pointerup", this._onRzUp)
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
    this.drawerTarget.classList.remove("is-resizing") // restores transition → animates to snap
    const mid = (this.#NARROW_WIDTH + this.#wideWidth()) / 2
    this.expanded = this._rzLiveW >= mid
    this.#persistExpanded()
    this.#applyOpenState()
  }

  // Keyboard parity with the sidebar handle.
  resizeKeydown(e) {
    if (e.key === "ArrowLeft") this.expanded = true
    else if (e.key === "ArrowRight") this.expanded = false
    else if (e.key === "Enter" || e.key === " ") this.expanded = !this.expanded
    else return
    e.preventDefault()
    this.#persistExpanded()
    this.#applyOpenState()
  }

  // ── Task 8: History / archive panel ──────────────────────────────────

  openHistory() {
    this.showHistory = true
    this.#renderHistory()
    this.historyPanelTarget.classList.remove("hidden")
  }

  closeHistory() {
    this.showHistory = false
    this.historyPanelTarget.classList.add("hidden")
  }

  newChat() {
    // Abandon any in-flight reply (matches cc-data.jsx newChat)
    this.#cancelStream()
    this.isReplying = false
    this.#updateSendState()

    const fresh = this.#ccNewConvo()
    // Drop any unused "New debrief" that was never interacted with, so we don't pile up empties
    this.convos = [
      fresh,
      ...this.convos.filter(
        (c) =>
          !(
            c.title === "New debrief" &&
            !(c.messages || []).some((m) => m.role === "user")
          ),
      ),
    ]
    this.activeId = fresh.id
    this.#renderMessages()
    this.closeHistory()
  }

  async openConvo(e) {
    const id = e.currentTarget.dataset.convoId
    if (!id) return
    this.#cancelStream()
    this.isReplying = false
    this.#updateSendState()
    this.activeId = id
    this.closeHistory()

    const convo = this.convos.find((c) => c.id === id)
    if (convo && convo.serverId != null && !convo.loaded) {
      this.#renderMessages() // show empty while loading
      try {
        const res = await fetch(`/coach/conversations/${convo.serverId}`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        })
        if (res.ok) {
          const data = await res.json()
          convo.messages = (data.messages || []).map((m) => ({
            ...m,
            createdAt: m.createdAt || 0,
          }))
          convo.loaded = true
        }
      } catch {
        /* leave empty on failure */
      }
    }
    this.#renderMessages()
  }

  // ── Task 7: input + quick replies ─────────────────────────────────────

  sendFromInput() {
    if (!this.hasInputTarget) return
    const text = this.inputTarget.value
    this.#send(text)
    this.inputTarget.value = ""
    this.#resizeInput()
    this.inputTarget.focus()
  }

  onInputKeydown(e) {
    // Enter submits; Shift+Enter inserts a newline. Ignore Enter mid-IME-composition.
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault()
      this.sendFromInput()
    }
  }

  autoGrow() {
    this.#resizeInput()
  }

  // Grow the textarea with its content up to MAX_INPUT_HEIGHT, then scroll.
  #resizeInput() {
    if (!this.hasInputTarget) return
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT)}px`
  }

  quickReply(e) {
    this.#send(e.currentTarget.dataset.q)
  }

  // Retry a failed turn in place: reset the errored bubble to pending and
  // re-stream the same prompt (the user message that preceded it). The server
  // drops the failed turn so it's replaced, not duplicated.
  retryReply(e) {
    if (this.isReplying) return
    const replyId = e.currentTarget.dataset.replyId
    const convo = this.convos.find((c) => c.id === this.activeId)
    if (!convo) return
    const idx = (convo.messages || []).findIndex((m) => m.id === replyId)
    if (idx < 0) return
    let prompt = null
    for (let i = idx - 1; i >= 0; i--) {
      if (convo.messages[i].role === "user") {
        prompt = convo.messages[i].body
        break
      }
    }
    if (!prompt) return

    this.#patchMsg(this.activeId, replyId, {
      errored: false,
      body: "",
      pending: true,
      tools: [],
      toolsDone: false,
      priorDraft: null,
      insights: null,
      drills: null,
      lapRefs: null,
      sectorDelta: null,
      suggestedReplies: null,
      sectionMap: null,
    })
    this.isReplying = true
    this.#updateSendState()
    this.#renderMessages()
    this.#streamReply(this.activeId, replyId, prompt, convo.serverId, true)
  }

  confirmDelete(e) {
    e.stopPropagation()
    const id = e.currentTarget.dataset.convoId
    const overlay = this.historyPanelTarget.querySelector(
      `[data-cc-confirm="${CSS.escape(id)}"]`,
    )
    if (overlay) overlay.classList.remove("hidden")
  }

  cancelDelete(e) {
    e.stopPropagation()
    const id = e.currentTarget.dataset.convoId
    const overlay = this.historyPanelTarget.querySelector(
      `[data-cc-confirm="${CSS.escape(id)}"]`,
    )
    if (overlay) overlay.classList.add("hidden")
  }

  async performDelete(e) {
    e.stopPropagation()
    const id = e.currentTarget.dataset.convoId
    const convo = this.convos.find((c) => c.id === id)
    if (!convo || convo.serverId == null) return
    const csrf =
      document.querySelector('meta[name="csrf-token"]')?.content || ""
    try {
      await fetch(`/coach/conversations/${convo.serverId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrf, Accept: "application/json" },
        credentials: "same-origin",
      })
    } catch {
      return
    }
    this.convos = this.convos.filter((c) => c.id !== id)
    const lostActive = this.activeId === id
    if (this.convos.length === 0) {
      const fresh = this.#ccNewConvo()
      this.convos = [fresh]
      this.activeId = fresh.id
    } else if (lostActive) {
      const fresh = this.#ccNewConvo()
      this.convos = [fresh, ...this.convos]
      this.activeId = fresh.id
    }
    if (lostActive || this.convos.length === 1) this.#renderMessages()
    this.#renderHistory()
  }

  loadOlder() {
    if (this.#historyCursor) this.#loadHistory(this.#historyCursor)
  }

  // ── Task 5: toggle tool-call trace open/closed (event-delegated, no re-render)
  toggleToolTrace(e) {
    const box = e.currentTarget.closest("[data-trace]")
    if (box) box.classList.toggle("cc-trace-open")
  }

  // Expand/collapse the "earlier draft" glimpse box (superseded by a critic revision).
  toggleDraft(e) {
    const box = e.currentTarget.closest("[data-draft]")
    if (box) box.classList.toggle("cc-draft-open")
  }

  // ── Task 6: lap-chip click → select that lap in the laps panel via SessionStore
  selectLap(e) {
    const n = Number(e.currentTarget.dataset.lap)
    if (Number.isFinite(n)) SessionStore.set("activeLapNumber", n)
  }

  // ── send + SSE reply stream (the live coaching seam) ──

  // #send: append user + pending coach bubble, then stream the real reply.
  // Guard: no-op if empty or a reply is already in flight.
  #send(prompt) {
    const text = (prompt || "").trim()
    if (!text || this.isReplying) return

    const convoId = this.activeId
    const userId = this.#ccUid("u")
    const replyId = this.#ccUid("c")

    // Append user + pending coach message; title the convo on the first user msg.
    this.convos = this.convos.map((c) => {
      if (c.id !== convoId) return c
      const isFirstUser = !(c.messages || []).some((m) => m.role === "user")
      return {
        ...c,
        title: isFirstUser
          ? text.length > 38
            ? text.slice(0, 37) + "…"
            : text
          : c.title,
        updatedAt: Date.now(),
        messages: [
          ...(c.messages || []),
          { id: userId, role: "user", body: text, createdAt: Date.now() },
          {
            id: replyId,
            role: "coach",
            body: "",
            pending: true,
            tools: [],
            toolsDone: false,
            createdAt: Date.now(),
          },
        ],
      }
    })

    this.isReplying = true
    this.#updateSendState()
    this.#renderMessages()

    const active = this.convos.find((c) => c.id === convoId)
    this.#streamReply(convoId, replyId, text, active ? active.serverId : null)
  }

  // Abort the in-flight SSE fetch, if any.
  #cancelStream() {
    if (this.#abort) {
      this.#abort.abort()
      this.#abort = null
    }
  }

  // #streamReply — the Slice-2 seam: POST the prompt and read the SSE stream,
  // patching the pending coach message as frames arrive.
  #streamReply(convoId, replyId, prompt, conversationId, retry = false) {
    this.#abort = new AbortController()
    const csrf =
      document.querySelector('meta[name="csrf-token"]')?.content || ""
    const body =
      conversationId == null
        ? { prompt }
        : { prompt, conversation_id: conversationId }
    if (retry) body.retry = true

    fetch(`/sessions/${this.sessionIdValue}/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
      credentials: "same-origin",
      body: JSON.stringify(body),
      signal: this.#abort.signal,
    })
      .then((res) => {
        if (!res.ok || !res.body)
          throw new Error(`stream failed: ${res.status}`)
        return this.#readStream(res.body, convoId, replyId)
      })
      .catch((e) => {
        if (e.name === "AbortError") return // user closed/switched — leave state to that handler
        this.#failReply(convoId, replyId)
      })
  }

  // Finalize with a friendly error if no SSE frame arrives for this long — guards
  // against a dropped/hung connection leaving the bubble stuck in "Working".
  #WATCHDOG_MS = 90000

  #armWatchdog(convoId, replyId) {
    this.#clearWatchdog()
    this.#watchdog = setTimeout(() => {
      if (!this.isReplying) return
      this.#cancelStream()
      this.#failReply(convoId, replyId)
    }, this.#WATCHDOG_MS)
  }

  #clearWatchdog() {
    if (this.#watchdog) {
      clearTimeout(this.#watchdog)
      this.#watchdog = null
    }
  }

  async #readStream(body, convoId, replyId) {
    const reader = body.getReader()
    const decoder = new TextDecoder()
    let buf = ""
    this.#armWatchdog(convoId, replyId)

    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      this.#armWatchdog(convoId, replyId) // reset on activity
      buf += decoder.decode(value, { stream: true })
      // SSE frames are separated by a blank line. The server always terminates
      // each frame with "\n\n", so an untrimmed tail is an incomplete frame we
      // intentionally drop.
      let sep
      while ((sep = buf.indexOf("\n\n")) !== -1) {
        const frame = buf.slice(0, sep)
        buf = buf.slice(sep + 2)
        this.#dispatchFrame(convoId, replyId, frame)
      }
    }

    this.#clearWatchdog()
    // Stream closed without a terminal done/error frame — don't strand the input.
    // The #abort check avoids double-finalizing when a terminal frame already ran
    // #finishReply (which nulls #abort).
    if (this.isReplying && this.#abort) {
      this.#patchMsg(convoId, replyId, { pending: false, toolsDone: true })
      this.#finishReply()
    }
  }

  // Parse one "event: <type>\ndata: <json>" frame and apply it to the message.
  #dispatchFrame(convoId, replyId, frame) {
    let event = null
    let data = {}
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) {
        event = line.slice(6).trim()
      } else if (line.startsWith("data:")) {
        try {
          data = JSON.parse(line.slice(5).trim())
        } catch {
          data = {}
        }
      }
    }
    if (!event) return

    switch (event) {
      case "conversation":
        this.convos = this.convos.map((c) =>
          c.id === convoId
            ? { ...c, serverId: data.id, title: data.title || c.title }
            : c,
        )
        break
      case "widgets":
        this.#patchMsg(convoId, replyId, {
          insights: data.insights,
          drills: data.drills,
          lapRefs: data.lapRefs,
          sectorDelta: data.sectorDelta,
        })
        this.#renderMessages()
        break
      case "suggestions":
        this.#patchMsg(convoId, replyId, { suggestedReplies: data.replies })
        this.#renderMessages()
        break
      case "section_map":
        this.#patchMsg(convoId, replyId, { sectionMap: data })
        this.#renderMessages()
        break
      case "tool_call":
        this.#patchMsg(convoId, replyId, (m) => ({
          tools: [
            ...(m.tools || []),
            {
              id: data.id,
              kind: data.kind,
              label: data.label,
              detail: data.detail,
              status: "running",
            },
          ],
        }))
        this.#renderMessages()
        break
      case "tool_result":
        this.#patchMsg(convoId, replyId, (m) => ({
          tools: (m.tools || []).map((t) =>
            t.id === data.id ? { ...t, status: "done" } : t,
          ),
        }))
        this.#renderMessages()
        break
      case "text_delta":
        // First delta flips toolsDone → the streaming caret appears.
        this.#patchMsg(convoId, replyId, (m) => ({
          toolsDone: true,
          body: (m.body || "") + (data.text || ""),
        }))
        this.#renderMessages()
        break
      case "revising":
        // Critic asked for a revision. Tuck the just-streamed draft into a
        // compact, collapsed "earlier draft" box (a 2-line glimpse) instead of
        // wiping it inline, then stream the revised answer fresh into the body.
        this.#patchMsg(convoId, replyId, (m) => ({
          priorDraft: m.body || m.priorDraft || "",
          body: "",
          insights: null,
          drills: null,
          lapRefs: null,
          sectorDelta: null,
          suggestedReplies: null,
          sectionMap: null,
          pending: true,
          toolsDone: false,
        }))
        this.#renderMessages()
        break
      case "error":
        this.#patchMsg(convoId, replyId, {
          body: data.message || "Something went wrong. Try again.",
          pending: false,
          toolsDone: true,
          errored: true,
          priorDraft: null,
        })
        this.#finishReply()
        break
      case "done":
        // Also flip toolsDone (like the error/stream-close paths): a turn can
        // end with tools but no text deltas, which must still resolve the trace.
        this.#patchMsg(convoId, replyId, { pending: false, toolsDone: true })
        this.#finishReply()
        break
    }
  }

  // Network/parse failure: show a friendly message and re-enable input.
  #failReply(convoId, replyId) {
    this.#patchMsg(convoId, replyId, {
      body: "I couldn't reach the engineer just now. Try again in a moment.",
      pending: false,
      toolsDone: true,
      errored: true,
      priorDraft: null,
    })
    this.#finishReply()
  }

  #finishReply() {
    this.#clearWatchdog()
    this.#abort = null
    this.isReplying = false
    this.#updateSendState()
    this.#renderMessages()
    this.#loadHistory() // refresh rows/snippets (no-op visually unless History is open)
  }

  // #patchMsg: immutably update one message (by id) inside one convo (by id).
  // Only the targeted convo's updatedAt is bumped; other convos are untouched.
  #patchMsg(convoId, msgId, patch) {
    this.convos = this.convos.map((c) => {
      if (c.id !== convoId) return c
      return {
        ...c,
        updatedAt: Date.now(),
        messages: (c.messages || []).map((m) => {
          if (m.id !== msgId) return m
          const updates = typeof patch === "function" ? patch(m) : patch
          return { ...m, ...updates }
        }),
      }
    })
  }

  // #updateSendState: disable/enable the send button to match isReplying.
  #updateSendState() {
    if (!this.hasSendBtnTarget) return
    this.sendBtnTarget.disabled = this.isReplying
    this.sendBtnTarget.classList.toggle("opacity-60", this.isReplying)
    this.sendBtnTarget.classList.toggle("cursor-not-allowed", this.isReplying)
    this.sendBtnTarget.classList.toggle("cursor-pointer", !this.isReplying)
  }

  // GET the user's persisted conversations and merge into this.convos (after the
  // client-only fresh thread). Lightweight rows; messages load lazily on reopen.
  async #loadHistory(cursor = null) {
    try {
      const url = cursor
        ? `/coach/conversations?cursor=${encodeURIComponent(cursor)}`
        : "/coach/conversations"
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!res.ok) return
      const data = await res.json()
      const rows = (data.conversations || []).map((c) =>
        this.#historyRowToConvo(c),
      )
      const fresh = this.convos.filter((c) => c.serverId == null)
      const existing = this.convos.filter((c) => c.serverId != null)
      const byId = new Map(existing.map((c) => [c.serverId, c]))
      rows.forEach((r) => {
        const prev = byId.get(r.serverId)
        if (!prev) {
          byId.set(r.serverId, r)
          return
        }
        byId.set(
          r.serverId,
          prev.loaded
            ? { ...r, id: prev.id, messages: prev.messages, loaded: true }
            : { ...prev, ...r, id: prev.id },
        )
      })
      this.convos = [...fresh, ...byId.values()]
      if (!this.convos.some((c) => c.id === this.activeId)) {
        this.activeId = this.convos[0]?.id
      }
      this.#historyCursor = data.next_cursor || null
      if (this.showHistory) this.#renderHistory()
    } catch {
      /* offline / transient — leave the fresh thread */
    }
  }

  #historyRowToConvo(c) {
    return {
      id: `srv_${c.id}`,
      serverId: c.id,
      title: c.title,
      snippet: c.snippet,
      sessionLabel: c.session_label,
      updatedAt: Date.parse(c.updated_at) || Date.now(),
      messages: null, // lazy
      loaded: false,
    }
  }

  // ── Task 4: message model + declarative render pipeline ───────────────

  #activeConvo() {
    return this.convos.find((c) => c.id === this.activeId) || this.convos[0]
  }

  #renderMessages() {
    if (!this.hasMessagesListTarget) return
    const convo = this.#activeConvo()
    const msgs = convo.messages || []
    const bubblesHtml = msgs
      .map((m) =>
        m.role === "coach" ? this.#coachBubble(m) : this.#userBubble(m),
      )
      .join("")

    // Opener pills on a fresh convo; otherwise follow-up pills under the latest
    // settled coach reply (cleared automatically once the next turn appends a
    // new pending message).
    const hasUserMsg = msgs.some((m) => m.role === "user")
    const last = msgs[msgs.length - 1]
    let pillsHtml = ""
    if (!hasUserMsg && !this.isReplying) {
      const openers = this.openersValue?.length
        ? this.openersValue
        : CC_QUICK_REPLIES
      pillsHtml = this.#quickReplyRow(openers)
    } else if (
      last &&
      last.role === "coach" &&
      !last.pending &&
      last.suggestedReplies &&
      last.suggestedReplies.length > 0
    ) {
      pillsHtml = this.#quickReplyRow(last.suggestedReplies)
    }

    // Preserve mounted section-map widgets across the innerHTML rebuild: detach
    // their nodes (keeps the instance + listeners + sampled SVG + hover state),
    // rebuild, then re-attach — a move, not a re-render, so no thrash.
    this._sectionMaps.forEach((inst) => {
      if (inst.el) inst.el.remove()
    })
    this.messagesListTarget.innerHTML = bubblesHtml + pillsHtml
    this.#mountSectionMaps(msgs)
    this.messagesListTarget.scrollTop = this.messagesListTarget.scrollHeight
  }

  // Mount/re-attach a SectionMap per [data-sm-slot] in the freshly-rebuilt list,
  // and destroy instances whose message/slot is gone (convo switch, deletion).
  #mountSectionMaps(msgs) {
    const byId = new Map(msgs.map((m) => [String(m.id), m]))
    const liveIds = new Set()
    this.messagesListTarget
      .querySelectorAll("[data-sm-slot]")
      .forEach((slot) => {
        const id = slot.dataset.smSlot
        const msg = byId.get(id)
        if (!msg || !msg.sectionMap) return
        liveIds.add(id)
        let inst = this._sectionMaps.get(id)
        if (!inst) {
          inst = new SectionMap(msg.sectionMap)
          inst.mount(slot)
          this._sectionMaps.set(id, inst)
        } else {
          slot.appendChild(inst.el) // re-attach the preserved node (no rebuild)
        }
      })
    for (const [id, inst] of this._sectionMaps) {
      if (!liveIds.has(id)) {
        inst.destroy()
        this._sectionMaps.delete(id)
      }
    }
  }

  #destroySectionMaps() {
    this._sectionMaps.forEach((inst) => inst.destroy())
    this._sectionMaps.clear()
  }

  // ── Task 8: History panel HTML render ────────────────────────────────

  #renderHistory() {
    if (!this.hasHistoryPanelTarget) return

    // Sort convos by updatedAt descending (most-recent first)
    const sorted = [...this.convos].sort((a, b) => b.updatedAt - a.updatedAt)
    const activeId = this.activeId

    const rows = sorted
      .map((c) => {
        const active = c.id === activeId
        const used =
          c.serverId != null ||
          (c.messages && c.messages.some((m) => m.role === "user"))
        const title = used
          ? this.#escapeHtml(c.title || "Untitled")
          : "New debrief"

        // Last message with a body, searched in reverse (fall back to snippet from server row)
        const lastMsg = c.messages
          ? [...c.messages].reverse().find((m) => m.body)
          : null
        const snippetText = lastMsg
          ? lastMsg.body
          : c.snippet || "No messages yet"
        const snippet = this.#escapeHtml(snippetText)

        const rowCls = active
          ? "bg-vivid-purple/[0.10] ring-vivid-purple/30"
          : "bg-app-surface ring-app-border-subtle hover:bg-app-surface-hover hover:ring-app-border"
        const titleCls = active ? "text-vivid-purple" : "text-app-text"

        const activeBadge = active
          ? `<span class="shrink-0 font-mono text-[8.5px] tracking-[0.14em] px-1.5 py-0.5 rounded bg-vivid-purple/20 text-vivid-purple">ACTIVE</span>`
          : ""

        const delAffordance =
          c.serverId == null
            ? ""
            : `
        <button type="button" title="Delete chat"
                data-action="coaching-chat#confirmDelete"
                data-convo-id="${this.#escapeHtml(c.id)}"
                class="absolute top-1.5 right-1.5 z-[5] h-6 w-6 rounded-md flex items-center justify-center text-app-text-faint hover:text-red-500 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition cursor-pointer">
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /></svg>
        </button>`

        return `
        <div class="group relative rounded-xl ring-1 transition w-full ${rowCls}">
          <button type="button"
                  data-action="coaching-chat#openConvo"
                  data-convo-id="${this.#escapeHtml(c.id)}"
                  class="block text-left px-3 py-2.5 cursor-pointer w-full">
            <div class="flex items-center gap-2">
              <span class="text-[12.5px] font-semibold truncate flex-1 ${titleCls}">${title}</span>
              ${activeBadge}
              <span class="shrink-0 text-[10px] text-app-text-faint transition group-hover:opacity-0">${this.#ccRelTime(c.updatedAt)}</span>
            </div>
            <div class="mt-0.5 text-[11px] text-app-text-muted line-clamp-1 truncate">${snippet}</div>
          </button>
          ${delAffordance}
          <div data-cc-confirm="${this.#escapeHtml(c.id)}" class="hidden absolute inset-0 z-10 rounded-xl bg-app-bg/95 flex items-center justify-end gap-2 px-3">
            <span class="text-[11px] text-app-text-muted mr-auto">Delete this chat?</span>
            <button type="button" data-action="coaching-chat#cancelDelete" data-convo-id="${this.#escapeHtml(c.id)}"
                    class="h-7 px-2.5 rounded-md text-[11px] font-semibold text-app-text-secondary ring-1 ring-app-border-subtle hover:bg-app-surface-hover transition cursor-pointer">Cancel</button>
            <button type="button" data-action="coaching-chat#performDelete" data-convo-id="${this.#escapeHtml(c.id)}"
                    class="h-7 px-2.5 rounded-md text-[11px] font-semibold text-white bg-red-600 hover:bg-red-500 transition cursor-pointer">Delete</button>
          </div>
        </div>`
      })
      .join("")

    const loadMore = this.#historyCursor
      ? `
      <button type="button" data-action="coaching-chat#loadOlder"
              class="mt-1 h-8 rounded-lg text-[12px] font-semibold text-app-text-secondary ring-1 ring-app-border-subtle hover:bg-app-surface-hover transition cursor-pointer">
        Load older
      </button>`
      : ""

    const backBtn = `
      <button type="button"
              data-action="coaching-chat#closeHistory"
              title="Back"
              class="h-7 w-7 rounded-lg flex items-center justify-center text-app-text-muted hover:bg-app-surface-hover hover:text-app-text transition cursor-pointer shrink-0">
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>`

    const newChatBtn = `
      <button type="button"
              data-action="coaching-chat#newChat"
              class="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-vivid-purple/15 ring-1 ring-vivid-purple/30 text-[12px] font-semibold text-vivid-purple hover:bg-vivid-purple/25 transition cursor-pointer shrink-0">
        <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
        New chat
      </button>`

    this.historyPanelTarget.innerHTML = `
      <div class="absolute inset-0 z-20 bg-app-bg flex flex-col cc-slidein">
        <div class="px-3.5 h-[56px] flex items-center gap-2.5 border-b border-app-border-subtle shrink-0">
          ${backBtn}
          <div class="flex-1 text-[14.5px] font-semibold text-app-text">Chats</div>
          ${newChatBtn}
        </div>
        <div class="flex-1 overflow-y-auto scrollbar-thin p-2.5 flex flex-col gap-1.5">
          ${rows}
          ${loadMore}
        </div>
        <div class="px-3.5 py-2.5 border-t border-app-border-subtle text-[10.5px] text-app-text-faint shrink-0">
          Chats are kept per session and synced across your devices.
        </div>
      </div>`
  }

  // ── CoachBubble: avatar + [TOOL TRACES — Task 5] + body + [WIDGETS — Task 6]
  #coachBubble(m) {
    const {
      body,
      insights,
      drills,
      lapRefs,
      sectorDelta,
      sectionMap,
      pending,
      tools,
      toolsDone,
      priorDraft,
      errored,
    } = m
    const hasTools = tools && tools.length > 0
    const showText = toolsDone || body

    const toolsHtml = hasTools ? this.#toolTrace(tools, !!toolsDone) : ""

    // "Thinking" dots: the gap after sending, before the first tool trace or text
    // delta arrives (nothing else would render, leaving an empty bubble).
    const thinking = pending && !hasTools && !body
    const thinkingHtml = thinking
      ? `<div class="flex items-center gap-1 text-app-text-faint py-0.5" aria-label="Thinking">
           <span class="cc-typing-dot"></span>
           <span class="cc-typing-dot" style="animation-delay:.16s"></span>
           <span class="cc-typing-dot" style="animation-delay:.32s"></span>
         </div>`
      : ""

    // "Earlier draft" box: a critic revision tucked the superseded draft into a
    // compact, collapsed glimpse (first ~2 lines, fading out). While the revised
    // answer hasn't started streaming yet (pending && !body), the header shows a
    // "Refining…" spinner instead.
    const draftHtml = priorDraft
      ? this.#draftPreview(priorDraft, pending && !body)
      : ""

    const caretHtml =
      pending && toolsDone
        ? `<span class="text-app-text-faint cc-cursor">&#x258d;</span>`
        : ""

    const textHtml = showText
      ? `<div class="cc-md text-[13px] leading-[1.5] text-app-text${hasTools || priorDraft ? " mt-2" : ""}" style="text-wrap:pretty">${this.#renderMarkdown(body || "")}${caretHtml}</div>`
      : ""

    // Task 6: rich widgets
    const bestLapN = (this.sessionValue || {}).bestLapN
    const lapRefsHtml =
      lapRefs && lapRefs.length > 0 ? this.#lapRefsRow(lapRefs, bestLapN) : ""
    const sectorDeltaHtml = sectorDelta
      ? this.#sectorDeltaCard(sectorDelta)
      : ""
    const insightsHtml =
      insights && insights.length > 0 ? this.#insightPillsRow(insights) : ""
    const drillsHtml =
      drills && drills.length > 0 ? this.#drillCardsRow(drills) : ""
    const sectionMapHtml = sectionMap
      ? `<div data-sm-slot="${this.#escapeHtml(String(m.id))}" class="mt-2.5"></div>`
      : ""

    // Retry pill on a failed turn: re-runs the same prompt, replacing this bubble.
    const retryHtml = errored
      ? `<div class="mt-2">
           <button type="button"
                   data-action="coaching-chat#retryReply"
                   data-reply-id="${this.#escapeHtml(String(m.id))}"
                   class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-app-surface-alt ring-1 ring-app-border-subtle text-[11.5px] font-medium text-app-text-secondary hover:ring-vivid-purple/40 hover:text-app-text transition cursor-pointer">
             <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>
             Retry
           </button>
         </div>`
      : ""

    return `
      <div class="flex gap-2.5 px-4 mb-3.5 items-start">
        ${this.#coachAvatarFixed(28)}
        <div class="rounded-2xl rounded-tl-md bg-app-surface ring-1 ring-app-border-subtle px-3.5 py-2.5 max-w-[84%] min-w-0">
          ${thinkingHtml}
          ${toolsHtml}
          ${draftHtml}
          ${textHtml}
          ${lapRefsHtml}
          ${sectorDeltaHtml}
          ${insightsHtml}
          ${drillsHtml}
          ${sectionMapHtml}
          ${retryHtml}
        </div>
      </div>`
  }

  // ── UserBubble: right-aligned; body is HTML-escaped to prevent injection
  #userBubble(m) {
    return `
      <div class="flex justify-end px-4 mb-3.5">
        <div class="rounded-2xl rounded-tr-md bg-vivid-purple/[0.14] ring-1 ring-vivid-purple/30 px-3.5 py-2.5 max-w-[78%]">
          <p class="text-[13px] leading-[1.5] text-app-text whitespace-pre-wrap">${this.#escapeHtml(m.body || "")}</p>
        </div>
      </div>`
  }

  // ── Task 5: Tool-call trace HTML helpers ─────────────────────────────

  // ── Task 7: Reply bank + quick replies (ported from cc-data.jsx) ──────

  // StatusDot: done = green check, running = spinning ring, pending = faint ring.
  #statusDot(status) {
    if (status === "done")
      return `<svg class="h-3 w-3 text-live shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7" /></svg>`
    if (status === "running")
      return `<span class="h-3 w-3 shrink-0 rounded-full border-[1.5px] border-vivid-purple/30 border-t-vivid-purple cc-spin" style="display:inline-block;"></span>`
    // pending
    return `<span class="h-3 w-3 shrink-0 rounded-full ring-1 ring-app-border-strong" style="display:inline-block;"></span>`
  }

  // ToolIcon: SVG icon for the given tool kind, falling back to "compute".
  #toolIcon(kind) {
    const d = TOOL_ICON[kind] || TOOL_ICON.compute
    return `<svg class="h-3 w-3 text-app-text-faint shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="${d}" /></svg>`
  }

  // ToolTrace: header row (spinner/"Working…" while running, EngMark/"Ran N steps" when done)
  // + collapsible body listing each tool row. Collapsed when done, open while running.
  // The outer [data-trace] wrapper + toggleToolTrace event delegation controls open/closed
  // via the .cc-trace-open class (no full re-render required).
  #toolTrace(tools, done) {
    if (!tools || tools.length === 0) return ""
    const running = !done || tools.some((t) => t.status === "running")
    const n = tools.length
    const openClass = running ? "cc-trace-open" : ""

    const headerIcon = running
      ? `<span class="h-3 w-3 block rounded-full border-[1.5px] border-vivid-purple/30 border-t-vivid-purple cc-spin shrink-0"></span>`
      : `<svg class="h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 14v-2a7 7 0 0 1 14 0v2" /><rect x="3.2" y="13" width="3.6" height="6" rx="1.6" fill="currentColor" stroke="none" /><rect x="17.2" y="13" width="3.6" height="6" rx="1.6" fill="currentColor" stroke="none" /><path d="M19 19v.5a3 3 0 0 1-3 3h-2.5" /></svg>`

    const headerLabel = running
      ? "Working…"
      : `Ran ${n} step${n > 1 ? "s" : ""}`

    const toolRows = tools
      .map((t) => {
        const opacity = t.status === "pending" ? "opacity:0.45;" : ""
        const detail = t.detail
          ? `<span class="ml-auto font-mono text-[9.5px] text-app-text-faint truncate max-w-[44%]">${this.#escapeHtml(t.detail)}</span>`
          : ""
        return `
        <div class="flex items-center gap-2" style="${opacity}">
          ${this.#statusDot(t.status || "pending")}
          ${this.#toolIcon(t.kind)}
          <span class="text-[11px] text-app-text-secondary truncate">${this.#escapeHtml(t.label || "")}</span>
          ${detail}
        </div>`
      })
      .join("")

    return `
      <div data-trace class="${openClass} rounded-lg bg-app-surface-alt/70 ring-1 ring-app-border-subtle overflow-hidden">
        <button type="button"
                data-action="coaching-chat#toggleToolTrace"
                class="w-full flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-app-surface-hover/50 transition">
          <span class="text-vivid-purple/80 shrink-0 flex items-center">${headerIcon}</span>
          <span class="text-[11px] font-medium text-app-text-muted">${headerLabel}</span>
          <svg class="cc-trace-chevron ml-auto h-3 w-3 text-app-text-faint transition-transform"
               viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        <div class="cc-trace-body px-2.5 pb-2 pt-1.5 flex-col gap-1.5 border-t border-app-border-subtle">
          ${toolRows}
        </div>
      </div>`
  }

  // DraftPreview: the superseded draft from a critic revision, shown as a compact
  // collapsed box (a 2-line glimpse fading out). Collapsed by default; click the
  // header to expand. `inProgress` = the revised answer hasn't started streaming
  // yet, so the header shows a spinner + "Refining…".
  #draftPreview(text, inProgress) {
    if (!text) return ""
    const headerIcon = inProgress
      ? `<span class="h-3 w-3 block rounded-full border-[1.5px] border-vivid-purple/30 border-t-vivid-purple cc-spin shrink-0"></span>`
      : `<svg class="h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5h12M4 10h12M4 15h7"/></svg>`
    const headerLabel = inProgress ? "Refining your answer…" : "Earlier draft"
    return `
      <div data-draft class="mt-1.5 rounded-lg bg-app-surface-alt/70 ring-1 ring-app-border-subtle overflow-hidden">
        <button type="button"
                data-action="coaching-chat#toggleDraft"
                class="w-full flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-app-surface-hover/50 transition">
          <span class="text-vivid-purple/80 shrink-0 flex items-center">${headerIcon}</span>
          <span class="text-[11px] font-medium text-app-text-muted">${headerLabel}</span>
          <svg class="cc-draft-chevron ml-auto h-3 w-3 text-app-text-faint transition-transform"
               viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        <div class="cc-draft-body px-2.5 pb-2 pt-1.5 border-t border-app-border-subtle">
          <div class="cc-md text-[12px] leading-[1.5] text-app-text-muted" style="text-wrap:pretty">${this.#renderMarkdown(text)}</div>
        </div>
      </div>`
  }

  // ── Task 6: Rich widget HTML helpers ─────────────────────────────────

  // LapLink: the chip with a CSS-hover-only preview popover (no JS state).
  // The outer <span> is the "group" — hover CSS in application.css reveals
  // the sibling popover via `.cc-lap-link:hover .cc-lap-popover { display: block }`.
  #lapLink(n, bestLapN) {
    const lapByN = this.#ccLapByN()
    const lap = lapByN[n]
    const isBest = n === bestLapN
    const role = isBest ? "best" : "ref"

    const tone = isBest
      ? "bg-vivid-purple/10 ring-vivid-purple/30 text-vivid-purple hover:bg-vivid-purple/20"
      : "bg-app-surface-active/50 ring-app-border text-app-text-secondary hover:ring-vivid-purple/40 hover:text-app-text"

    const roleBadgeCls = isBest
      ? "bg-vivid-purple/20 text-vivid-purple"
      : "bg-app-surface-active text-app-text-muted"
    const timeCls = isBest
      ? "text-vivid-purple"
      : lap && lap.invalid
        ? "text-app-text-faint"
        : "text-app-text"

    let popoverBody = ""
    if (lap) {
      const secs =
        !lap.invalid && lap.s1 && lap.s2 && lap.s3
          ? `<span class="mt-1.5 grid grid-cols-3 gap-1">
             ${[
               ["S1", lap.s1, "#FF2E92"],
               ["S2", lap.s2, "#A78BFA"],
               ["S3", lap.s3, "#14F4D9"],
             ]
               .map(
                 ([k, v, c]) => `
               <span class="block">
                 <span class="block font-mono text-[8px] tracking-wider" style="color:${c}">${k}</span>
                 <span class="block font-mono text-[10px] tabular-nums text-app-text-secondary">${v}</span>
               </span>`,
               )
               .join("")}
           </span>`
          : `<span class="block mt-1 font-mono text-[10px] text-neon-magenta/80">Invalid · no sectors</span>`

      popoverBody = `
        <span class="cc-lap-popover absolute bottom-full left-0 mb-1.5 z-30 w-[176px] rounded-lg bg-app-popover-bg ring-1 ring-app-border-strong p-2.5 pointer-events-none shadow-[0_12px_32px_rgba(0,0,0,0.5)]" style="display:none;">
          <span class="flex items-center justify-between">
            <span class="font-mono text-[9px] tracking-[0.18em] uppercase text-app-text-faint">Lap ${n}</span>
            <span class="font-mono text-[8px] tracking-[0.12em] uppercase px-1 py-0.5 rounded ${roleBadgeCls}">${role}</span>
          </span>
          <span class="block mt-1 font-mono text-[16px] font-bold tabular-nums ${timeCls}">${lap.time || "—"}</span>
          ${secs}
          <span class="mt-1.5 flex items-center gap-1 text-[9.5px] text-app-text-muted">
            <span class="h-2 w-2 rounded-full ring-1 ring-neon-yellow-orange" style="display:inline-block;"></span> ${this.#escapeHtml(lap.compound || "")} · open in analysis
          </span>
        </span>`
    }

    return `
      <span class="cc-lap-link relative inline-flex">
        <button type="button"
                data-action="coaching-chat#selectLap"
                data-lap="${n}"
                class="inline-flex items-center gap-1.5 rounded-md ring-1 px-1.5 py-0.5 font-mono text-[10.5px] cursor-pointer transition ${tone}">
          <svg class="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M18 20V10M12 20V4M6 20v-6" stroke-linecap="round" /></svg>
          <span class="font-semibold tabular-nums">L${n}</span>
          <span class="text-[8.5px] tracking-[0.12em] uppercase ${isBest ? "text-vivid-purple/70" : "text-app-text-faint"}">${role}</span>
        </button>
        ${popoverBody}
      </span>`
  }

  // Row of lap-ref chips
  #lapRefsRow(lapRefs, bestLapN) {
    if (!lapRefs || lapRefs.length === 0) return ""
    return `<div class="flex flex-wrap gap-1.5 mt-2">${lapRefs.map((n) => this.#lapLink(n, bestLapN)).join("")}</div>`
  }

  // InsightPill: colored dot + label tag
  #insightPill(tag) {
    return `
      <span class="inline-flex items-center gap-1.5 rounded-md bg-white/[0.04] ring-1 ring-app-border px-2 py-1 text-[11px] font-semibold" style="color:${this.#escapeHtml(tag.color || "#fff")}">
        <span class="h-1.5 w-1.5 shrink-0 self-center rounded-full" style="background:${this.#escapeHtml(tag.color || "#fff")}; display:inline-block;"></span>
        ${this.#escapeHtml(tag.label || "")}
      </span>`
  }

  // Row of insight pills
  #insightPillsRow(insights) {
    if (!insights || insights.length === 0) return ""
    return `<div class="flex flex-wrap gap-1.5 mt-2.5">${insights.map((t) => this.#insightPill(t)).join("")}</div>`
  }

  // DrillCard: colored-border card with title + detail
  #drillCard(d) {
    const bg = d.color ? `${d.color}14` : "rgba(167,139,250,0.08)"
    const ring = d.color ? `${d.color}33` : "rgba(167,139,250,0.2)"
    return `
      <div class="flex-1 rounded-lg px-3 py-2.5" style="background:${bg}; box-shadow:inset 0 0 0 1px ${ring}">
        <div class="text-[11.5px] font-bold leading-tight" style="color:${this.#escapeHtml(d.color || "#A78BFA")}">${this.#escapeHtml(d.title || "")}</div>
        <div class="mt-1 font-mono text-[10px] text-app-text-muted leading-snug">${this.#escapeHtml(d.detail || "")}</div>
      </div>`
  }

  // Row of drill cards
  #drillCardsRow(drills) {
    if (!drills || drills.length === 0) return ""
    return `<div class="flex gap-2 mt-2.5">${drills.map((d) => this.#drillCard(d)).join("")}</div>`
  }

  // SectorDeltaCard: YOU vs REF/TARGET bar chart.
  // unit "ms" → ccFmtLap, unit "s" → toFixed(3), otherwise Math.round + unit suffix.
  #sectorDeltaCard(d) {
    const isLap = d.unit === "ms"
    const fmt = (v) => {
      if (isLap) return this.#ccFmtLap(v)
      if (d.unit === "s") return Number(v).toFixed(3)
      return `${Math.round(v)} ${this.#escapeHtml(d.unit || "")}`
    }
    const youColor = d.best || d.improve ? "#A78BFA" : "#FF2E92"
    const refColor = "#8b8fa5"
    const max = Math.max(d.you || 0, d.ref || 0) || 1
    const refLabel = d.improve ? "TARGET" : "REF"

    const rows = [
      ["YOU", d.you, youColor],
      [refLabel, d.ref, refColor],
    ]
      .map(
        ([k, v, c]) => `
      <div class="flex items-center gap-2">
        <span class="font-mono text-[9px] w-10 shrink-0" style="color:${c}">${k}</span>
        <div class="flex-1 h-1.5 rounded-full bg-app-surface-active overflow-hidden">
          <div class="h-full rounded-full" style="width:${((v / max) * 100).toFixed(1)}%; background:${c}; opacity:0.85;"></div>
        </div>
        <span class="font-mono text-[11px] tabular-nums w-14 text-right" style="color:${c}">${fmt(v)}</span>
      </div>`,
      )
      .join("")

    return `
      <div class="mt-2.5 rounded-lg bg-app-surface-alt ring-1 ring-app-border-subtle px-3 py-2.5">
        <div class="font-mono text-[9px] tracking-[0.18em] uppercase text-app-text-faint mb-2">${this.#escapeHtml(d.label || "")}</div>
        <div class="flex flex-col gap-1.5">${rows}</div>
      </div>`
  }

  // ── CoachAvatarFixed HTML helper
  #coachAvatarFixed(size = 28) {
    const glyph = Math.round(size * 0.54)
    return `
      <div class="shrink-0 rounded-full flex items-center justify-center text-white ring-1 ring-white/20"
           style="width:${size}px; height:${size}px; background:linear-gradient(180deg,#A78BFA 0%,#825CE8 100%); box-shadow:0 4px 12px rgba(106,0,255,0.35);">
        <svg style="width:${glyph}px; height:${glyph}px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 14v-2a7 7 0 0 1 14 0v2" />
          <rect x="3.2" y="13" width="3.6" height="6" rx="1.6" fill="currentColor" stroke="none" />
          <rect x="17.2" y="13" width="3.6" height="6" rx="1.6" fill="currentColor" stroke="none" />
          <path d="M19 19v.5a3 3 0 0 1-3 3h-2.5" />
        </svg>
      </div>`
  }

  // ── Security: escape user-supplied text before inserting into innerHTML
  #escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

  // Minimal, XSS-safe markdown → HTML for coach replies. Escapes the whole string
  // FIRST (so any injected HTML is inert), then applies a constrained subset the
  // persona is told to use: **bold**, *italic*/_italic_, `code`, "- "/"1." lists,
  // and blank-line-separated paragraphs. No raw HTML, headings, tables, or images.
  #renderMarkdown(raw) {
    const inline = (s) =>
      s
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>")
        .replace(/(^|[^_])_([^_\n]+)_(?!_)/g, "$1<em>$2</em>")
        .replace(
          /`([^`]+)`/g,
          '<code class="font-mono text-[11px] px-1 py-0.5 rounded bg-app-surface-active">$1</code>',
        )

    const lines = this.#escapeHtml(String(raw || "")).split("\n")
    const out = []
    let list = null
    let para = []
    const flushList = () => {
      if (!list) return
      const cls = list.tag === "ol" ? "list-decimal" : "list-disc"
      out.push(
        `<${list.tag} class="${cls} pl-4 my-1 space-y-0.5">${list.items.map((i) => `<li>${i}</li>`).join("")}</${list.tag}>`,
      )
      list = null
    }
    const flushPara = () => {
      if (para.length === 0) return
      out.push(`<p class="my-1">${para.join(" ")}</p>`)
      para = []
    }
    for (const line of lines) {
      const t = line.trim().replace(/^#{1,6}\s+/, "") // strip any heading marker → plain emphasis line
      if (t === "") {
        flushList()
        flushPara()
        continue
      }
      const ul = t.match(/^[-*]\s+(.*)$/)
      const ol = t.match(/^\d+[.)]\s+(.*)$/)
      if (ul) {
        flushPara()
        if (!list || list.tag !== "ul") {
          flushList()
          list = { tag: "ul", items: [] }
        }
        list.items.push(inline(ul[1]))
      } else if (ol) {
        flushPara()
        if (!list || list.tag !== "ol") {
          flushList()
          list = { tag: "ol", items: [] }
        }
        list.items.push(inline(ol[1]))
      } else {
        flushList()
        para.push(inline(t))
      }
    }
    flushList()
    flushPara()
    return out.join("")
  }

  // ── Task 7: QuickReplyRow HTML helper ────────────────────────────────

  #quickReplyRow(replies = CC_QUICK_REPLIES) {
    const chips = replies
      .map(
        (q) =>
          `<button type="button"
               data-action="coaching-chat#quickReply"
               data-q="${this.#escapeHtml(q)}"
               class="px-3 py-1.5 rounded-full bg-app-surface-alt ring-1 ring-app-border-subtle text-[11.5px] font-medium text-app-text-secondary hover:ring-vivid-purple/40 hover:text-app-text transition cursor-pointer">
         ${this.#escapeHtml(q)}
       </button>`,
      )
      .join("")
    return `<div class="flex flex-wrap gap-2 px-4 pt-1 pb-2">${chips}</div>`
  }

  // ── Data helpers ported from cc-data.jsx ──────────────────────────────
  // Driven off this.sessionValue / this.lapsValue (not the hard-coded CC_SESSION/CC_LAPS).

  #ccFmtLap(ms) {
    const m = Math.floor(ms / 60000)
    const s = ((ms % 60000) / 1000).toFixed(3)
    return `${m}:${s.padStart(6, "0")}`
  }

  #ccRelTime(ts) {
    const d = Date.now() - ts
    const min = Math.round(d / 60000)
    const hr = Math.round(d / 3600000)
    const day = Math.round(d / 86400000)
    if (min < 1) return "just now"
    if (min < 60) return `${min}m ago`
    if (hr < 24) return `${hr}h ago`
    if (day === 1) return "yesterday"
    return `${day}d ago`
  }

  #ccUid(prefix = "id") {
    this.__ccIdSeq = (this.__ccIdSeq || 0) + 1
    return `${prefix}${this.__ccIdSeq}-${Math.random().toString(36).slice(2, 6)}`
  }

  // Build the briefing message body + metadata, parameterised on real session data.
  #ccBriefing(s) {
    // Guard: fall back to empty session shape if values haven't loaded yet.
    const track = s.trackShort || s.track || "this track"
    const kind = (s.kind || "Session").toLowerCase()
    const car = s.carShort || s.car || "your car"
    const best = s.bestLapMs ? this.#ccFmtLap(s.bestLapMs) : "—"
    const bestN = s.bestLapN || "—"
    const lapCnt = s.laps || 0
    // Honest opener: only real session facts (no fabricated findings/tool-trace).
    // The agent produces real insights/drills once the driver asks something.
    const bestStr = s.bestLapMs ? ` Your best is ${best} on L${bestN}.` : ""
    return {
      body: `I've loaded your ${kind} at ${track} — ${lapCnt} lap${lapCnt === 1 ? "" : "s"} in the ${car}.${bestStr} Ask me anything about this session, or pick a question below.`,
      lapRefs: s.bestLapN ? [s.bestLapN] : [],
    }
  }

  // Build the lap lookup map from lapsValue (mirrors CC_LAP_BY_N).
  #ccLapByN() {
    const laps = this.lapsValue || []
    return Object.fromEntries(laps.map((l) => [l.n, l]))
  }

  // Create a new conversation with the briefing seeded as the first coach message.
  #ccNewConvo(seeded = true) {
    const s = this.sessionValue || {}
    const m0 = {
      id: this.#ccUid("m"),
      role: "coach",
      createdAt: Date.now(),
      ...this.#ccBriefing(s),
    }
    // Briefing tools are already "done" — the agent read the session on open.
    m0.tools = (m0.tools || []).map((t) => ({ ...t, status: "done" }))
    m0.toolsDone = true
    return {
      id: this.#ccUid("conv"),
      serverId: null,
      title: "New debrief",
      messages: seeded ? [m0] : [],
      loaded: true,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
  }
}
